import { React, useState, useEffect } from "react";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useCookies } from "react-cookie";
import axios from "axios";
import jobHubimg from "../images/jobHub.png";

function Login() {
  // The CAS details
  const casClient = {
    service_url: encodeURIComponent(
      "https://jobhub.cs.vt.edu/cas-redirect"
    ),
    server_url: "https://login.cs.vt.edu/cas/login",
  };

  // loginMode === null => show two buttons
  // loginMode === "employer" => show employer login form
  const [loginMode, setLoginMode] = useState(null);

  // State for toggling password visibility
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword((prev) => !prev);

  const navigate = useNavigate();
  const [cookies, setCookie, removeCookie] = useCookies([
    "auth_token",
    "redirectJobId",
  ]);
  const [loadingJobFromURL, setLoadingJobFromURL] = useState(false);

  // For login errors
  const [loginError, setLoginError] = useState("");

  useEffect(() => {
    // Extract Job ID from URL and store it in a cookie if present
    const queryParams = new URLSearchParams(window.location.search);
    const jobId = queryParams.get("Jid");
    if (jobId) {
      setLoadingJobFromURL(true);
      setCookie("redirectId", jobId, { path: "/" });
    }
  }, [cookies, navigate, setCookie, removeCookie]);

  useEffect(() => {
    if (loadingJobFromURL) {
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.delete("Jid");
      const newUrl = window.location.pathname;
      window.history.pushState(null, "", newUrl);
      setLoadingJobFromURL(false); // Reset the loading state
    }
  }, [loadingJobFromURL]);

  // ---------------------------
  // CAS Login (CS Students)
  // ---------------------------
  function handleLoginCAS() {
    const casLoginUri =
      casClient.server_url + "?service=" + casClient.service_url;
    window.location.href = casLoginUri;
  }

  // ---------------------------
  // Employer Login Logic
  // ---------------------------
  const handleEmployeeLogin = (event) => {
    event.preventDefault();
    setLoginError(""); // Clear any existing error message

    const body = new FormData();
    body.append("username", event.target.username.value);
    body.append("password", event.target.password.value);

    axios
      .post("/login", body)
      .then((res) => {
        const data = res.data;
        if (data.success) {
          const expiration = new Date(data.expiration);
          setCookie("auth_token", data.token, {
            path: "/",
            expires: expiration,
          });
          // Reload page
          navigate(0);
        } else {
          setLoginError("Invalid Username or Password");
        }
      })
      .catch((err) => {
        console.error("Login error:", err);
        setLoginError("Invalid Username or Password");
      });
  };

  // The Employer Login Form
  function renderEmployerLoginForm() {
    return (
      <div className="Login-employer flex flex-col">
        <form onSubmit={handleEmployeeLogin}>
          <div className="Login-employerText text-center font-brand-primary font-bold text-5xl mb-7">
            Employer login
          </div>

          {/* Username Field */}
          <div className="Login-employerUsername h-11 w-80 border mx-auto rounded-lg flex mb-4 border-brand-secondary">
            <div className="my-auto mx-2 text-brand-secondary">
              <PersonIcon size={24} />
            </div>
            <input
              type="text"
              className="h-full w-full bg-transparent font-brand-tertiary font-light text-md placeholder-gray-500"
              placeholder="USERNAME"
              name="username"
              maxLength="50"
              required
            />
          </div>

          {/* Password Field */}
          <div className="Login-employerUsername h-11 w-80 border mx-auto rounded-lg flex mb-4 border-brand-secondary">
            <div className="my-auto mx-2 text-brand-secondary">
              <LockIcon size={24} />
            </div>
            <input
              type={showPassword ? "text" : "password"}
              className="h-full w-full bg-transparent font-brand-tertiary font-light text-md placeholder-gray-500"
              placeholder="PASSWORD"
              name="password"
              maxLength="50"
              required
            />
            <button
              type="button"
              className="my-auto mx-2"
              onClick={handleClickShowPassword}>
              {showPassword ? (
                <VisibilityIcon size={24} />
              ) : (
                <VisibilityOffIcon size={24} />
              )}
            </button>
          </div>

          {/* Login Button */}
          <button
            type="submit"
            className="Login-signInButton flex primary-button bg-brand-primary mx-auto">
            Login
          </button>

          {/* Error Message */}
          {loginError && (
            <div className="mt-4 text-center text-red-500 font-medium">
              {loginError}
            </div>
          )}

          {/* Register Button */}
          <button
            type="button"
            className="Login-signInButton flex primary-button bg-brand-primary mx-auto mt-4"
            onClick={() => navigate("/register")}>
            Register
          </button>

          {/* Forgot Password Button */}
          <button
            type="button"
            className="Login-forgotPassword text-brand-primary underline text-sm font-medium hover:text-brand-dark transition block mx-auto mt-2"
            onClick={() => navigate("/reset-password")}>
            Forgot Password?
          </button>
        </form>

        {/* Back Button to Return to Main (two-button) Page */}
        <button
          type="button"
          onClick={() => setLoginMode(null)} // Go back to main
          className="text-sm underline text-center text-brand-secondary mt-4">
          Back
        </button>
      </div>
    );
  }

  function renderMainLanding() {
    return (
      <div className="flex flex-col items-center p-6 gap-8">
        {/* CS Students */}
        <div className="flex flex-col items-center">
          <h3 className="text-xl font-semibold mb-2">CS Student & Faculty Login</h3>
          <button
            onClick={handleLoginCAS}
            className="Login-signInButton flex primary-button bg-brand-primary mx-auto text-xl px-8 py-4">
            CS Account
          </button>
        </div>

        {/* Employers */}
        <div className="flex flex-col items-center">
          <h3 className="text-xl font-semibold mb-2">Employer Login</h3>
          <button
            onClick={() => setLoginMode("employer")}
            className="Login-signInButton flex primary-button bg-brand-primary mx-auto text-xl px-8 py-4">
            Employers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="Homepage flex flex-col justify-center h-full">
      <div className="login-container">
        <img src={jobHubimg} className="jobHubImage" alt="Job Hub" />
        <div className="Homepage-page flex justify-center flex-col w-96 py-4 mx-auto rounded-2xl bg-white shadow-lg">
          <div className="Login text-center">
            {loginMode === null && renderMainLanding()}
            {loginMode === "employer" && renderEmployerLoginForm()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
